/**
 * Booking flow helpers (client). Server-side resolution lives in
 * DrCalvo_Backend/Utils/providerConfigUtil.js
 */

/**
 * Merge Charm visit type objects with provider-config order for a category.
 * @param {object} charmGrouped - { newpatient: [], established: [] } from GetVisitTypes
 * @param {object} provider - provider entry from GetProviderConfig
 * @param {"newpatient"|"established"} category
 * @returns {object[]} ordered full visit type rows for UI
 */
export function orderedVisitTypesForProvider(charmGrouped, provider, category) {
  if (!provider?.visit_types || !charmGrouped) return [];
  const cat = category === "established" ? "established" : "newpatient";
  const refs = provider.visit_types[cat] || [];
  const pool = charmGrouped[cat] || [];
  return refs
    .map((ref) =>
      pool.find((v) => String(v.visit_type_id) === String(ref.visit_type_id))
    )
    .filter(Boolean);
}

/**
 * Whether same-day and next-day should be blocked (after 3:00 PM America/Chicago).
 */
export function isAfterThreePmCST() {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Chicago",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
    });
    const parts = fmt.formatToParts(new Date());
    const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
    const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
    return hour > 15 || (hour === 15 && minute > 0);
  } catch {
    return false;
  }
}

/**
 * YYYY-MM-DD for "today" in America/Chicago (date-only string).
 */
export function todayDateStringCST() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * Dates blocked by the booking lead-time policy, as YYYY-MM-DD strings in
 * America/Chicago. After 3:00 PM CT, same-day and next-day are unavailable.
 *
 * Returns date-only strings on purpose: every caller compares these against
 * calendar cells or API slot keys, which are date-only too. Comparing zoned
 * timestamps instead shifts the block by a day for visitors outside CT.
 */
export function leadTimeBlockedDateStringsCST() {
  if (!isAfterThreePmCST()) return [];
  const today = todayDateStringCST();
  const nextDay = new Date(`${today}T00:00:00Z`);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  return [today, nextDay.toISOString().slice(0, 10)];
}
